import React, { useState } from 'react';
import { Form, Button, Row, Col, Card, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import { addOrder } from '../../services/firestore';

const CheckoutForm = () => {
  const navigate = useNavigate();
  const { items, total, clearCart } = useCart();
  const { currentUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    paymentMethod: 'credit_card',
  });

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    // Validate form
    if (!formData.firstName.trim()) {
      setError('First name is required');
      setSubmitting(false);
      return;
    }

    if (!formData.lastName.trim()) {
      setError('Last name is required');
      setSubmitting(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      setSubmitting(false);
      return;
    }

    if (!formData.phone.trim()) {
      setError('Phone number is required');
      setSubmitting(false);
      return;
    }

    if (!/^\d{11}$/.test(formData.phone.trim())) {
      setError('Phone number must be exactly 11 digits');
      setSubmitting(false);
      return;
    }

    if (!formData.address.trim()) {
      setError('Address is required');
      setSubmitting(false);
      return;
    }

    if (!formData.city.trim()) {
      setError('City is required');
      setSubmitting(false);
      return;
    }

    if (!formData.state.trim()) {
      setError('State is required');
      setSubmitting(false);
      return;
    }

    if (!formData.zipCode.trim()) {
      setError('Zip code is required');
      setSubmitting(false);
      return;
    }

    // if (!formData.country.trim()) {
    //   setError('Country is required');
    //   setSubmitting(false);
    //   return;
    // }

    try {
      // Prepare order data
      const orderData = {
        userId: currentUser ? currentUser.uid : 'guest',
        customerInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country
        },
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          imageUrl: item.imageUrl
        })),
        paymentMethod: formData.paymentMethod,
        total: total,
        status: 'pending' // Initial status is always 'pending'
      };

      // Save order to Firestore
      await addOrder(orderData);
      
      // Clear the cart
      clearCart();
      
      // Redirect to a success page or back to home
      navigate('/');
      
      // Show success message
      alert('Order placed successfully! Thank you for your purchase. Your order status is pending.');
      
      setSubmitting(false);
    } catch (err) {
      setError('An error occurred while processing your order. Please try again.');
      setSubmitting(false);
    }
  };

  // Back to cart
  const handleBackToCart = () => {
    navigate('/cart');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <h1 className="mb-4">Checkout</h1>
      
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      
      <Row>
        <Col md={8}>
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <h4 className="mb-3">Shipping Information</h4>
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="firstName">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        disabled={submitting}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="lastName">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        disabled={submitting}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="email">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={submitting}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="phone">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={submitting}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="address">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    disabled={submitting}
                    required
                  />
                </Form.Group>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="city">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        disabled={submitting}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="state">
                      <Form.Label>State/Province</Form.Label>
                      <Form.Control
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        disabled={submitting}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="zipCode">
                      <Form.Label>Zip/Postal Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        disabled={submitting}
                        required
                      />
                    </Form.Group>
                  </Col>
                  {/* <Col md={6}>
                    <Form.Group controlId="country">
                      <Form.Label>Country</Form.Label>
                      <Form.Select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        disabled={submitting}
                        required
                      >
                        <option value="Pakistan">Pakistan</option>
                        <option value="United States">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Australia">Australia</option>
                        <option value="Germany">Germany</option>
                        <option value="France">France</option>
                        <option value="Japan">Japan</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col> */}
                </Row>

                <h4 className="mb-3 mt-4">Payment Method</h4>
                <Form.Group className="mb-3">
                  {/* <Form.Check
                    type="radio"
                    id="credit_card"
                    name="paymentMethod"
                    value="credit_card"
                    label="Credit Card"
                    checked={formData.paymentMethod === 'credit_card'}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  <Form.Check
                    type="radio"
                    id="paypal"
                    name="paymentMethod"
                    value="paypal"
                    label="PayPal"
                    checked={formData.paymentMethod === 'paypal'}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  <Form.Check
                    type="radio"
                    id="bank_transfer"
                    name="paymentMethod"
                    value="bank_transfer"
                    label="Bank Transfer"
                    checked={formData.paymentMethod === 'bank_transfer'}
                    onChange={handleChange}
                    disabled={submitting}
                  /> */}
                  <Form.Check
                    type="radio"
                    id="cash_on_delivery"
                    name="paymentMethod"
                    value="cash_on_delivery"
                    label="Cash on Delivery"
                    checked={formData.paymentMethod === 'cash_on_delivery'}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </Form.Group>

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={handleBackToCart}
                    disabled={submitting}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Cart
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner size="sm" text="" />
                        <span className="ms-2">Processing...</span>
                      </>
                    ) : (
                      'Place Order'
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Order Summary</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                {items.map((item) => (
                  <div key={item.id} className="d-flex justify-content-between mb-2">
                    <span>
                      {item.name} <small className="text-muted">x{item.quantity}</small>
                    </span>
                    <span>{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>
              <hr />
              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal:</span>
                <span>{formatCurrency(total)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Shipping:</span>
                <span>Free</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between mb-0 fw-bold">
                <span>Total:</span>
                <span className="text-primary fs-5">{formatCurrency(total)}</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </motion.div>
  );
};

export default CheckoutForm;
